import React from "react";
import { GoalStatus } from "@pages/Program/utils/enums";
import { MilestoneDisplay } from "@shared/interfaces/ProgramFiles";
import Pill from "@shared/components/Pill";

interface MilestoneUpdate {
  id: number;
  description: string;
  source: string;
  applied_at: string;
  applied_by?: {
    id: number;
    name: string;
  } | null;
}

type UpdateEntry = MilestoneUpdate | "No Updates";

interface MilestoneCardProps {
  milestone: MilestoneDisplay;
  detailed?: boolean;
  onClick?: () => void;
  updates: UpdateEntry[];
}

const statusColorMap: Record<MilestoneDisplay["status"], string> = {
  on_track: "text-[#009947] border-[#009947] bg-[#EAFBF2] border",
  behind: "text-[#F29F04] border-[#F29F04] bg-[#FFF6E6] border",
  at_risk: "text-[#F04438] border-[#F04438] bg-[#FEF3F2] border",
};

const formatDateToLongString = (dateInput: string | Date): string => {
  const date = new Date(dateInput);
  const day = date.getDate();
  const daySuffix =
    day % 10 === 1 && day !== 11
      ? "st"
      : day % 10 === 2 && day !== 12
      ? "nd"
      : day % 10 === 3 && day !== 13
      ? "rd"
      : "th";
  const month = date.toLocaleString("en-US", { month: "long" });
  const year = date.getFullYear();
  return `${month} ${day}${daySuffix}, ${year}`;
};

const formatDateShort = (dateInput: string | Date): string =>
  new Date(dateInput).toLocaleDateString("en-GB", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

const MilestoneCard: React.FC<MilestoneCardProps> = ({
  milestone,
  detailed = false,
  onClick,
  updates,
}) => {
  const hasUpdates =
    updates && updates.length > 0 && updates[0] !== "No Updates";

  const latestUpdate =
    hasUpdates && updates[0] !== "No Updates"
      ? (updates[0] as MilestoneUpdate)
      : null;

  return (
    <div
      className={`border rounded-lg p-4 space-y-4 ${
        onClick ? "cursor-pointer hover:bg-gray-50" : ""
      }`}
      onClick={onClick}
    >
      {!detailed && (
        <div className="flex justify-between items-center">
          <h3 className="font-semibold">{milestone.name}</h3>
          <div className="whitespace-nowrap">
            <Pill
              styling={`${statusColorMap[milestone.status]}`}
              text={GoalStatus[milestone.status]}
            />
          </div>
        </div>
      )}

      <p className="text-sm text-gray-800">{milestone.description}</p>

      <div className="text-sm space-y-3">
        {milestone.responsible_user && (
          <div className="flex items-center space-x-2">
            <img src="/account_circle.svg" className="w-5" alt="user icon" />
            <p>
              {milestone.responsible_user.first_name}{" "}
              {milestone.responsible_user.last_name}
            </p>
          </div>
        )}

        <div className="flex items-center space-x-2">
          <img src="/calendar_today.svg" className="w-5" alt="calendar icon" />
          <p>
            Dates: {formatDateToLongString(milestone.start_date)} –{" "}
            {formatDateToLongString(milestone.end_date)}
          </p>
        </div>

        {detailed ? (
          <div className="flex flex-col space-y-2">
            <div className="flex items-center space-x-2">
              <img
                src="/notifications_active.svg"
                className="w-5"
                alt="update icon"
              />
              <p>Latest Updates:</p>
            </div>
            <div className="flex flex-col space-y-2 pl-7">
              {!hasUpdates ? (
                <p className="text-gray-500 italic">
                  No recent updates available for this task.
                </p>
              ) : (
                updates.map((update) => {
                  if (update === "No Updates") return null;
                  const u = update as MilestoneUpdate;
                  return (
                    <ul key={u.id} className="text-sm list-disc">
                      <li className="text-sm text-gray-500">{u.description}</li>
                    </ul>
                  );
                })
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-start space-x-2">
            <img
              src="/notifications_active.svg"
              className="w-5"
              alt="update icon"
            />
            {latestUpdate ? (
              <p className="text-ellipsis">
                Latest Update:{" "}
                <span className="text-gray-500">
                  {latestUpdate.description}
                </span>
              </p>
            ) : (
              <p className="text-gray-500 italic">
                No recent updates available for this task.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MilestoneCard;
